import { FC, useEffect, useState } from "react";
import { FieldInputProps } from "react-final-form";
import { useSelector } from "react-redux";

import { SegmentedControl, SpacingToken } from "@livechat/design-system-react-components";

import { CannedResponsePicker } from "components/canned-response-picker/CannedResponsePicker";
import Flex from "components/layout/Flex";
import { ICannedResponse, selectAgentCannedResponses } from "store/entities/canned-responses";
import {
  IRuleActionMessage,
  IRuleActionSendFollowUp,
  IRuleActionSendRatingRequest,
} from "store/entities/rules/interfaces";
import { withVar } from "themes/helpers";

import { MessageField } from "../../components/MessageField";
import { changeActionValues } from "../utils";

type MessageBasedAction = IRuleActionMessage | IRuleActionSendRatingRequest | IRuleActionSendFollowUp;
interface IProps {
  input: FieldInputProps<MessageBasedAction>;
}

enum MessageType {
  PlainText = "plain_text",
  CannedResponse = "canned_response",
}

export const Message: FC<IProps> = ({ input }) => {
  const cannedResponses = useSelector(selectAgentCannedResponses);

  const { value } = input;
  const { type, cannedResponseID, template } = value;
  const textValue = template?.text || "";
  const cannedValue = cannedResponseID ? cannedResponses[cannedResponseID]?.id || "" : "";

  const [messageType, setMessageType] = useState<MessageType>(getInitialMessageType(input.value));
  const [rememberedMessage, setRememberedMessage] = useState<{ text: string; cannedResponseID: string }>({
    text: textValue,
    cannedResponseID: cannedValue,
  });

  const changeText = (text: string): void => {
    const newValue = {
      isPrivate: "isPrivate" in value ? value.isPrivate : undefined,
      template: { text },
    };
    changeActionValues(input, newValue, { overwrite: true });
  };

  const changeCannedResponse = (cannedResponse: ICannedResponse | null): void => {
    const newValue = {
      isPrivate: "isPrivate" in value ? value.isPrivate : undefined,
      cannedResponseID: cannedResponse?.id,
    };
    changeActionValues(input, newValue, { overwrite: true });
  };

  const rememberMessage = (data: Partial<typeof rememberedMessage>): void =>
    setRememberedMessage(old => ({ ...old, ...data }));

  useEffect(() => {
    setRememberedMessage({
      text: textValue,
      cannedResponseID: cannedValue,
    });
    setMessageType(getInitialMessageType(value));
  }, [type]);

  const onMessageTypeChange = (type: MessageType): void => {
    setMessageType(type);
    if (type === MessageType.PlainText) {
      changeText(rememberedMessage.text);
    } else {
      changeCannedResponse(cannedResponses[rememberedMessage.cannedResponseID] || null);
    }
  };

  const onTemplateChange = (value: string): void => {
    rememberMessage({ text: value });
    changeText(value);
  };

  const onCannedResponseChange = (cannedResponse: ICannedResponse | null): void => {
    rememberMessage({ cannedResponseID: cannedResponse?.id || "" });
    changeCannedResponse(cannedResponse);
  };

  return (
    <Flex column width="100%" gridColumn="1/4" gap={withVar(SpacingToken.Spacing4)}>
      <Flex>
        <SegmentedControl
          buttons={[
            {
              label: "Custom message",
              id: MessageType.PlainText,
            },
            {
              label: "Canned response",
              id: MessageType.CannedResponse,
            },
          ]}
          onButtonClick={type => onMessageTypeChange(type as MessageType)}
          currentId={messageType}
        />
      </Flex>
      {messageType === MessageType.PlainText ? (
        <MessageField value={textValue} onChange={onTemplateChange} testId="message-plaintext" />
      ) : (
        <CannedResponsePicker
          value={cannedResponses[cannedValue]}
          cannedResponses={cannedResponses}
          onChange={onCannedResponseChange}
          testId="message-canned-response"
          placeholder="Search canned response"
        />
      )}
    </Flex>
  );
};

const getInitialMessageType = (initialValue: MessageBasedAction): MessageType =>
  initialValue?.template ? MessageType.PlainText : MessageType.CannedResponse;
